import React from "react";
import { Lock, ArrowRight, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { startGoogleLogin } from "@/lib/auth";
import { Link } from "react-router-dom";
import { recordExperimentEvent } from "@/lib/api";

/**
 * Soft paywall shown to logged-out readers below the first 2-3 paragraphs
 * of a blog post. Records A/B click events and hands off to the existing
 * Google sign-in flow which lands users on Stripe checkout (trial_period_days,
 * cancel_at_period_end, no refunds).
 *
 * Also surfaces up to 3 related essays underneath so a paywalled visitor
 * still has a warm rabbit hole rather than a dead end.
 */
export default function BlogSignupGate({ slug, title, variant, sessionId, related = [] }) {
  const track = (event) => {
    if (!variant || !sessionId) return;
    recordExperimentEvent({
      variant,
      event,
      session_id: sessionId,
      post_slug: slug,
    });
  };

  const beginTrial = () => {
    track("cta_click");
    try {
      window.sessionStorage.setItem("umbra_post_return", `/blog/${slug}`);
    } catch { /* sessionStorage disabled */ }
    startGoogleLogin();
  };

  const previewLabel = variant === "3p" ? "3 paragraphs" : "2 paragraphs";

  return (
    <div
      className="relative -mt-32 pt-32 pb-2 pointer-events-none"
      aria-hidden="true"
    >
      {/* Fade-to-black gradient over the last visible paragraph */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-transparent via-obsidian-900/80 to-obsidian-900"
      />
      <div
        className="pointer-events-auto relative mt-8 border border-terracotta/40 bg-obsidian-800/60 backdrop-blur-sm p-6 sm:p-10 text-center"
        data-testid="blog-paywall"
        role="region"
        aria-label="Continue reading"
      >
        <div className="inline-flex items-center gap-2 font-sans text-[0.6rem] uppercase tracking-[0.32em] text-terracotta mb-4">
          <Lock className="h-3 w-3" aria-hidden="true" />
          Continue reading
        </div>
        <h2 className="font-serif text-3xl sm:text-4xl text-bone-100 leading-tight mb-4 max-w-2xl mx-auto">
          The rest of this essay is behind a quiet door.
        </h2>
        <p className="font-serif italic text-bone-300 text-lg leading-relaxed mb-2 max-w-xl mx-auto">
          Start a 7-day free trial to finish reading{title ? ` "${title}"` : ""} —
          plus the full seven-chapter voice workbook.
        </p>
        <p className="font-sans text-xs text-bone-500 mb-8 max-w-xl mx-auto">
          Card required to start. No charge for 7 days. Cancel anytime — you keep
          access until the end of the current month. No refunds, no clawbacks.
        </p>
        <div className="flex items-center gap-4 justify-center flex-wrap">
          <Button
            onClick={beginTrial}
            data-testid="blog-paywall-cta"
            className="rounded-none bg-terracotta hover:bg-terracotta-deep text-bone-100 font-sans tracking-wider text-xs uppercase px-8 py-6"
          >
            Start free trial
            <ArrowRight className="ml-3 h-4 w-4" aria-hidden="true" />
          </Button>
          <Link
            to="/pricing"
            data-testid="blog-paywall-pricing"
            className="font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-terracotta focus:outline-none focus-visible:underline"
          >
            See pricing
          </Link>
        </div>
        <p className="font-sans text-[0.65rem] uppercase tracking-[0.24em] text-bone-500 mt-8">
          Already a listener?{" "}
          <button
            onClick={() => { track("cta_click"); startGoogleLogin(); }}
            className="underline hover:text-terracotta focus:outline-none focus-visible:text-terracotta"
            data-testid="blog-paywall-signin"
          >
            Sign in
          </button>
        </p>
        {variant && (
          <p
            hidden
            data-testid="blog-paywall-variant"
            aria-hidden="true"
          >
            variant · {previewLabel}
          </p>
        )}
      </div>

      {related.length > 0 && (
        <div
          className="pointer-events-auto relative mt-10"
          data-testid="blog-paywall-related"
          aria-label="Related free essays"
        >
          <p className="font-sans uppercase tracking-[0.24em] text-[0.6rem] text-terracotta mb-4 inline-flex items-center gap-2">
            <BookOpen className="h-3 w-3" aria-hidden="true" />
            Or wander to another essay
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-3 gap-4 list-none p-0">
            {related.slice(0, 3).map((r) => (
              <li key={r.id}>
                <Link
                  to={`/blog/${r.slug}`}
                  data-testid={`blog-paywall-related-${r.slug}`}
                  className="group block border border-obsidian-600 bg-obsidian-800/40 p-4 h-full hover:border-terracotta/70 focus:outline-none focus-visible:border-terracotta transition-colors"
                >
                  <p className="font-sans text-[0.55rem] uppercase tracking-[0.24em] text-bone-500 mb-1">
                    {r.category}{r.reading_time ? ` · ${r.reading_time} min` : ""}
                  </p>
                  <h3 className="font-serif text-base text-bone-100 group-hover:text-terracotta transition-colors leading-snug mb-1">
                    {r.title}
                  </h3>
                  <p className="font-serif italic text-bone-500 text-xs leading-relaxed line-clamp-2">
                    {r.excerpt || r.meta_description}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
